import type { Step } from '../models/Step';
import type { Section } from '../models/Section';
import type { Group } from '../models/Group';
import type { Form } from '../models/Form';

export class SavedDataLoader {
  private loadedData: Form | null = null;

  constructor(private formData: string | Record<string, unknown> | undefined) {}

  public hasLoadedData(): boolean {
    return this.loadedData != null && !!this.loadedData.guid;
  }

  public load(): void {
    if (this.formData) {
      this.loadedData = this.loadFromObject(this.formData);
      if (this.loadedData === null) {
        this.loadedData = this.loadFromJson(
          typeof this.formData === 'string' ? this.formData : JSON.stringify(this.formData)
        );
      }
    }
  }

  public getFormGuid(): string {
    return this.hasLoadedData() ? this.loadedData!.guid : '';
  }

  public getNumOfGroups(id: string, def: number = 0): number {
    let count = 0;

    if (!this.hasLoadedData()) {
      return def;
    }

    if (!Array.isArray(this.loadedData!.steps)) {
      return def;
    }

    this.loadedData!.steps.forEach((step: Step) => {
      if (!Array.isArray(step.sections)) {
        return;
      }

      step.sections.forEach((section: Section) => {
        if (!Array.isArray(section.groups)) {
          return;
        }

        section.groups.forEach((g: Group) => {
          if (g.id === id) {
            count++;
          }
        });
      });
    });

    return count || def;
  }

  public getAnswer(id: string, group: Group): unknown {
    let answer: unknown = null;

    if (!this.hasLoadedData()) {
      return answer;
    }

    if (!Array.isArray(this.loadedData!.steps)) {
      return answer;
    }

    this.loadedData!.steps.forEach((step: Step) => {
      if (!Array.isArray(step.sections)) {
        return;
      }

      step.sections.forEach((section: Section) => {
        if (!Array.isArray(section.groups)) {
          return;
        }

        section.groups.forEach((g: Group) => {
          if (!Array.isArray(g.questions)) {
            return;
          }

          g.questions.forEach((question) => {
            if (question.id === id && g.id === group.id && g.index === group.index) {
              answer = question.answer;
            }
          });
        });
      });
    });

    return answer;
  }

  private loadFromJson(json: string): Form | null {
    try {
      if (json !== '' && json !== null && json !== undefined) {
        return JSON.parse(json) as Form;
      }
    } catch {
      return null;
    }

    return null;
  }

  private loadFromObject(obj: string | Record<string, unknown>): Form | null {
    try {
      const data = typeof obj === 'string' ? JSON.parse(obj) : obj;
      if (data && (data as Form).guid) {
        return data as Form;
      }
    } catch {
      return null;
    }

    return null;
  }
}
